import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, Calendar, User, Tag,
  Video, PlayCircle, Edit3, Trash2, ExternalLink,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import VideoPlayerModal from '../../components/common/VideoPlayerModal';
import Loading from '../../components/common/Loading';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import { explainerVideosAPI } from '../../services/api';
import './ExplainerVideoDetails.css';
import { formatDate } from '../../utils/dateFormatter';
import { CONTENT_CATEGORIES as defaultCategories } from '../../constants/enums';
import { BASE_URL } from '../../constants/config';

// Placeholder image URL
const PLACEHOLDER_IMAGE = `${BASE_URL}/uploads/assets/placehold_content.svg`;

const ExplainerVideoDetails = () => {
  const { id } = useParams();
  const videoId = id;
  const navigate = useNavigate();
  
  // Smart back navigation: use browser history only if navigated from within app
  const handleBackNavigation = () => {
    navigate(-1);
  };
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [contentAssignmentModalOpen, setContentAssignmentModalOpen] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const thumbnailInputRef = useRef(null);
  const [categories, setCategories] = useState(defaultCategories);
  const [savingEdit, setSavingEdit] = useState(false);
  const [addingToContent, setAddingToContent] = useState(false);
  const [removingFromContent, setRemovingFromContent] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    video_url: '',
    tags: '',
    status: '',
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        console.log('Fetching categories...');
        const categoriesRes = await explainerVideosAPI.getCategories();
        const fetchedCategories = categoriesRes.data.data;
        console.log('Fetched categories:', fetchedCategories);
        // Use fetched data if non-empty, otherwise fallback to defaults
        setCategories(fetchedCategories && fetchedCategories.length > 0 ? fetchedCategories : defaultCategories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        console.log('Using fallback categories');
        setCategories(defaultCategories);
      }
    };
    fetchOptions();
  }, []);

  const { showNotification, currentUser } = useAppContext();
  const { filteredExplainerVideos, filteredUsers, filteredContent, refreshData, loading, error } = useRoleData();

  if (loading) {
    return (
      <div className="empty-state">
        <Loading fullScreen={false} message="Loading video..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <h3>Error loading video</h3>
        <p>{error}</p>
        <button className="btn btn-outline" onClick={() => refreshData()}>
          Retry
        </button>
      </div>
    );
  }

  const video = filteredExplainerVideos.find((v) => v.videoId && v.videoId.replace(/-/g, '') === id);

  if (!video) {
    return (
      <div className="empty-state">
        <h3>Explainer video not found or you don't have access</h3>
        <button className="btn btn-outline" onClick={handleBackNavigation}>
          Back to Videos
        </button>
      </div>
    );
  }

  const hasThumbnail = video.thumbnail && typeof video.thumbnail === 'string' && video.thumbnail.trim();

  const formattedUploadDate = formatDate(video.uploadDate);
  const uploader = filteredUsers.find(u => u.id === video.uploadedBy || u.name === video.uploadedBy);
  const uploadedByName = video.uploadedByName || (uploader ? (uploader.firstName && uploader.lastName ? `${uploader.firstName} ${uploader.lastName}` : uploader.name) : (video.uploadedBy || 'Unknown'));

  const associatedContent = filteredContent.filter((c) => video.contentIds.includes(c.id));
  const availableContent = filteredContent.filter((c) => !video.contentIds.includes(c.id));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddToContent = async () => {
    if (!selectedContentId) {
      showNotification('Please select content to add video to', 'error');
      return;
    }

    setAddingToContent(true);
    try {
      await explainerVideosAPI.addVideoToContent(selectedContentId, videoId);
      showNotification(`Video added to content successfully!`, 'success');
      setSelectedContentId('');
      // Refresh data
      refreshData();
    } catch (error) {
      console.error('Failed to add video to content:', error);
      showNotification(
        error.response?.data?.error || 'Failed to add video to content. Please try again.',
        'error'
      );
    } finally {
      setAddingToContent(false);
    }
  };

  const handleRemoveFromContent = async (contentId) => {
    setRemovingFromContent(true);
    try {
      await explainerVideosAPI.removeVideoFromContent(contentId, videoId);
      showNotification(`Video removed from content successfully!`, 'success');
      // Refresh data
      refreshData();
    } catch (error) {
      console.error('Failed to remove video from content:', error);
      showNotification(
        error.response?.data?.error || 'Failed to remove video from content. Please try again.',
        'error'
      );
    } finally {
      setRemovingFromContent(false);
    }
  };

  const handleEditOpen = () => {
    if (!hasPermission(currentUser, PERMISSIONS.EDIT_EXPLAINER_VIDEOS)) {
      showNotification('You do not have permission to edit explainer videos', 'error');
      return;
    }
    setForm({
      title: video.title,
      description: video.description,
      category: video.category,
      video_url: video.videoUrl,
      tags: video.tags.join(', '),
      status: video.status,
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!hasPermission(currentUser, PERMISSIONS.EDIT_EXPLAINER_VIDEOS)) {
      showNotification('You do not have permission to edit explainer videos', 'error');
      return;
    }
    // Basic validation
    if (!form.title.trim() || !form.description.trim() || !form.category.trim() || !form.video_url.trim()) {
      showNotification('Title, description, category, and video URL are required', 'error');
      return;
    }

    setSavingEdit(true);
    try {
      await explainerVideosAPI.updateVideo(videoId, {
        title: form.title,
        description: form.description,
        category: form.category,
        video_url: form.video_url,
        tags: form.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        status: form.status,
      });
      showNotification(`Video "${form.title}" updated successfully!`, 'success');
      setEditModalOpen(false);
      // Refresh video data
      refreshData();
    } catch (error) {
      console.error('Failed to update video:', error);
      showNotification(
        error.response?.data?.error || 'Failed to update video. Please try again.',
        'error'
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = () => {
    if (!hasPermission(currentUser, PERMISSIONS.DELETE_EXPLAINER_VIDEOS)) {
      showNotification('You do not have permission to delete explainer videos', 'error');
      return;
    }
    setDeleteConfirmationText('');
    setDeleteConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    // Validate confirmation text
    if (deleteConfirmationText.trim() !== 'delete video') {
      showNotification('Please type "delete video" exactly to confirm deletion', 'error');
      return;
    }

    setIsDeleting(true);
    try {
      await explainerVideosAPI.deleteVideo(videoId);
      showNotification(`Video "${video.title}" deleted successfully`, 'success');
      setDeleteConfirmModalOpen(false);
      navigate('/explainer-videos');
    } catch (error) {
      console.error('Delete video error:', error);
      showNotification(error.response?.data?.error || 'Failed to delete video', 'error');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmationText('');
    }
  };

  const handleThumbnailChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showNotification('Please select a valid image file (JPEG, PNG, GIF, WEBP)', 'error');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('Image size should be less than 10MB', 'error');
      return;
    }

    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      await explainerVideosAPI.uploadThumbnail(videoId, formData);
      showNotification('Thumbnail updated successfully', 'success');
      // Refresh data to get new thumbnail URL
      refreshData();
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      showNotification(
        error.response?.data?.error || 'Failed to upload thumbnail. Please try again.',
        'error'
      );
    } finally {
      setUploadingThumbnail(false);
      // Reset file input
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  };

  const handleDeleteThumbnail = async () => {
    if (!hasPermission(currentUser, PERMISSIONS.EDIT_EXPLAINER_VIDEOS)) {
      showNotification('You do not have permission to delete thumbnails', 'error');
      return;
    }
    if (!window.confirm('Are you sure you want to delete the thumbnail? This will remove the current thumbnail image.')) {
      return;
    }
    try {
      await explainerVideosAPI.deleteThumbnail(videoId);
      showNotification('Thumbnail deleted successfully', 'success');
      // Refresh data to reflect removed thumbnail
      refreshData();
    } catch (error) {
      console.error('Failed to delete thumbnail:', error);
      showNotification(
        error.response?.data?.error || 'Failed to delete thumbnail. Please try again.',
        'error'
      );
    }
  };

  const openVideoPlayerModal = () => {
    setIsVideoModalOpen(true);
  };

  const closeVideoPlayerModal = () => {
    setIsVideoModalOpen(false);
  };

  return (
    <div className="explainer-video-details">
      {/* Back Navigation */}
      <div className="explainer-video-details__back-nav">
        <button className="btn btn-ghost explainer-video-details__back" onClick={handleBackNavigation} title="Go back to previous page">
          <ArrowLeft size={16} />
          Back
        </button>
        <span className="explainer-video-details__back-separator">|</span>
        <button className="btn btn-link explainer-video-details__back-parent" onClick={() => navigate('/explainer-videos')} title="Back to Explainer Videos">
          Explainer Videos
        </button>
      </div>

      {/* Hero */}
      <div className="explainer-video-details__hero card">
        <div className="explainer-video-details__hero-thumb">
          <img
            src={hasThumbnail ? video.thumbnail : PLACEHOLDER_IMAGE}
            alt={video.title}
          />
          <div className="explainer-video-details__hero-overlay" />
          {hasPermission(currentUser, PERMISSIONS.EDIT_EXPLAINER_VIDEOS) && (
            <>
              <input
                type="file"
                ref={thumbnailInputRef}
                onChange={handleThumbnailChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <div className="explainer-video-details__thumb-actions">
                <button
                  className="btn btn-outline btn-sm explainer-video-details__thumb-edit"
                  onClick={() => thumbnailInputRef.current?.click()}
                  disabled={uploadingThumbnail}
                >
                  {uploadingThumbnail ? 'Uploading...' : (hasThumbnail ? 'Change thumbnail' : 'Upload thumbnail')}
                </button>
                {hasThumbnail && (
                  <button
                    className="btn btn-danger btn-sm explainer-video-details__thumb-delete"
                    onClick={handleDeleteThumbnail}
                    disabled={uploadingThumbnail}
                  >
                    Delete thumbnail
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="explainer-video-details__hero-info">
          <div className="explainer-video-details__hero-badges">
            <span className="badge badge-green">{video.category}</span>
            <span className={`badge ${video.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
              {video.status}
            </span>
            <span className="badge badge-dark">{video.videoType}</span>
          </div>
          <h1 className="explainer-video-details__hero-title">{video.title}</h1>
          <p className="explainer-video-details__hero-desc">{video.description}</p>
          <div className="explainer-video-details__hero-meta">
            <span><Calendar size={14} /> Uploaded {formattedUploadDate}</span>
            <span><User size={14} /> By {uploadedByName}</span>
            <span><Video size={14} /> {video.videoType}</span>
          </div>
          <div className="explainer-video-details__hero-actions">
            <button
              className="btn btn-primary"
              onClick={openVideoPlayerModal}
            >
              <PlayCircle size={15} />
              Play Video
            </button>
            {(currentUser?.role === 'Administrator' || currentUser?.role === 'Instructor') && (
              <button className="btn btn-primary" onClick={() => setContentAssignmentModalOpen(true)}>
                <ExternalLink size={15} />
                Add to Content
              </button>
            )}
            {hasPermission(currentUser, PERMISSIONS.EDIT_EXPLAINER_VIDEOS) && (
              <button className="btn btn-outline" onClick={handleEditOpen}>
                <Edit3 size={15} />
                Edit Video
              </button>
            )}
            {hasPermission(currentUser, PERMISSIONS.DELETE_EXPLAINER_VIDEOS) && (
              <button className="btn btn-danger" onClick={handleDelete} disabled={isDeleting}>
                <Trash2 size={15} />
                {isDeleting ? 'Deleting...' : 'Delete Video'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="explainer-video-details__grid">
        {/* Metadata */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              Video Details
            </h3>
          </div>
          <div className="card-body">
            <div className="explainer-video-details__meta-list">
              <div className="explainer-video-details__meta-item">
                <div className="explainer-video-details__meta-icon">
                  <Video size={16} />
                </div>
                <div>
                  <span className="explainer-video-details__meta-label">Video Type</span>
                  <span className="explainer-video-details__meta-value">{video.videoType}</span>
                </div>
              </div>
              <div className="explainer-video-details__meta-item">
                <div className="explainer-video-details__meta-icon">
                  <Clock size={16} />
                </div>
                <div>
                  <span className="explainer-video-details__meta-label">Upload Date</span>
                  <span className="explainer-video-details__meta-value">{formattedUploadDate}</span>
                </div>
              </div>
              <div className="explainer-video-details__meta-item">
                <div className="explainer-video-details__meta-icon">
                  <User size={16} />
                </div>
                <div>
                  <span className="explainer-video-details__meta-label">Uploaded By</span>
                  <span className="explainer-video-details__meta-value">{uploadedByName}</span>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="explainer-video-details__tags-section">
              <div className="explainer-video-details__tags-header">
                <Tag size={14} />
                <span>Tags</span>
              </div>
              <div className="explainer-video-details__tags">
                {video.tags.map((t) => (
                  <span key={t} className="explainer-video-details__tag">#{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Associated Content */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              Used in Content
            </h3>
            <span className="badge badge-dark">{associatedContent.length}</span>
          </div>
          <div className="card-body">
            {associatedContent.length === 0 ? (
              <div className="empty-state">
                <p>Not assigned to any content yet</p>
              </div>
            ) : (
              <div className="explainer-video-details__modules">
                {associatedContent.map((c) => (
                  <div
                    key={c.id}
                    className="explainer-video-details__module-item"
                    onClick={() => navigate(`/content-library/${c.contentId ? c.contentId.replace(/-/g, '') : c.id}`, { state: { fromVideo: true } })}
                  >
                    <div className="explainer-video-details__module-icon">
                      <Video size={18} />
                    </div>
                    <div className="explainer-video-details__module-info">
                      <span className="explainer-video-details__module-title">{c.title}</span>
                      <div className="explainer-video-details__module-meta">
                        <span>{c.category}</span>
                        <span className={`badge ${c.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                          {c.status}
                        </span>
                      </div>
                    </div>
                    <ArrowLeft size={16} style={{ transform: 'rotate(180deg)', color: 'var(--text-muted)' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Video Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Explainer Video"
        size="lg"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setEditModalOpen(false)}>Cancel</button>

            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
              <Edit3 size={14} /> {savingEdit ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="form-group">
            <label className="form-label">Video Title *</label>
            <input
              className="form-input"
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g., Safety Induction Overview"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea
              className="form-textarea"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe what this video covers..."
              rows={4}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                className="form-select"
                name="category"
                value={form.category}
                onChange={handleChange}
                required
              >
                <option value="">Select category</option>
                {(categories || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Video URL *</label>
            <input
              className="form-input"
              type="url"
              name="video_url"
              value={form.video_url}
              onChange={handleChange}
              placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
              required
            />
            <small className="text-muted">YouTube, Vimeo, or direct video URL</small>
          </div>

          <div className="form-group">
            <label className="form-label">Tags (comma-separated)</label>
            <input
              className="form-input"
              type="text"
              name="tags"
              value={form.tags}
              onChange={handleChange}
              placeholder="e.g., safety, induction, onboarding"
            />
          </div>
        </div>
      </Modal>

      {/* Manage Module Assignments */}
      <Modal
        isOpen={contentAssignmentModalOpen}
        onClose={() => setContentAssignmentModalOpen(false)}
        title="Manage Content Assignments"
        size="lg"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setContentAssignmentModalOpen(false)}>Cancel</button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          {/* Currently Associated Content */}
          <div>
            <h4 className="section-title">Currently Associated Content</h4>
            {associatedContent.length === 0 ? (
              <div className="empty-state">
                <p>This video is not assigned to any content yet.</p>
              </div>
            ) : (
              <div className="explainer-video-details__modules">
                {associatedContent.map((c) => (
                  <div
                    key={c.id}
                    className="explainer-video-details__module-item"
                  >
                    <div className="explainer-video-details__module-icon">
                      <Video size={18} />
                    </div>
                    <div className="explainer-video-details__module-info">
                      <span className="explainer-video-details__module-title">{c.title}</span>
                      <div className="explainer-video-details__module-meta">
                        <span>{c.category}</span>
                        <span className={`badge ${c.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                          {c.status}
                        </span>
                      </div>
                    </div>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleRemoveFromContent(c.id)}
                      disabled={removingFromContent}
                      style={{ marginLeft: 'auto' }}
                    >
                      <Trash2 size={14} /> {removingFromContent ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add to Another Module */}
          <div>
            <h4 className="section-title">Add to Another Content</h4>
            {availableContent.length === 0 ? (
              <div className="empty-state">
                <p>No other content available to add this video.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Select Content</label>
                  <select
                    className="form-select"
                    value={selectedContentId}
                    onChange={(e) => setSelectedContentId(e.target.value)}
                  >
                    <option value="">Choose content...</option>
                    {availableContent.map((m) => (
                      <option key={m.id} value={m.id}>{m.title} ({m.category})</option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleAddToContent}
                  disabled={!selectedContentId || addingToContent}
                >
                  <ExternalLink size={14} /> {addingToContent ? 'Adding...' : 'Add to Content'}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Video Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModalOpen}
        onClose={() => {
          setDeleteConfirmModalOpen(false);
          setDeleteConfirmationText('');
        }}
        title="Delete Explainer Video"
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => {
              setDeleteConfirmModalOpen(false);
              setDeleteConfirmationText('');
            }}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Video'}
            </button>
          </div>
        }
      >
        {video && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p>
              You are about to delete <strong>{video.title}</strong>. This action cannot be undone.
            </p>
            <p>
              This will permanently remove the video and all associated data including thumbnail and module associations.
            </p>
            <div className="form-group">
              <label className="form-label">
                Type <strong>"delete video"</strong> to confirm:
              </label>
              <input
                className="form-input"
                type="text"
                placeholder="Type 'delete video' here"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                autoFocus
              />
              <small className="text-muted">
                This extra step prevents accidental deletion.
              </small>
            </div>
          </div>
        )}
      </Modal>
      <VideoPlayerModal
        video={video}
        isOpen={isVideoModalOpen}
        onClose={closeVideoPlayerModal}
      />
    </div>
  );
};

export default ExplainerVideoDetails;